import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography, Grid, Container, useMediaQuery, useTheme } from '@mui/material';
import { motion } from 'framer-motion';

// Function to generate points along a path
const generatePointsAlongPath = (startX, startY, endX, endY, numPoints, curvature = 100) => {
  const points = [];
  const midX = (startX + endX) / 2;
  const controlX = startX < endX ? midX : midX;
  const controlY = startY < endY ? startY - curvature : startY + curvature;
  
  for (let i = 0; i <= numPoints; i++) {
    const t = i / numPoints;
    // Quadratic Bezier curve formula
    const x = Math.pow(1-t, 2) * startX + 2 * (1-t) * t * controlX + Math.pow(t, 2) * endX;
    const y = Math.pow(1-t, 2) * startY + 2 * (1-t) * t * controlY + Math.pow(t, 2) * endY;
    points.push({ x, y });
  }
  
  return points;
};

const DottedLine = ({ startX, startY, endX, endY, numDots = 20, isVisible, direction, isMobile }) => {
  // Adjust coordinates for mobile
  const adjustedStartX = isMobile ? startX * 0.5 : startX;
  const adjustedEndX = isMobile ? endX * 0.5 : endX;
  const adjustedStartY = startY;
  const adjustedEndY = endY;
  const adjustedCurvature = isMobile ? (direction === 'right' ? 50 : -50) : (direction === 'right' ? 100 : -100);
  
  const points = generatePointsAlongPath(
    adjustedStartX, 
    adjustedStartY, 
    adjustedEndX, 
    adjustedEndY, 
    numDots,
    adjustedCurvature
  );
  
  return (
    <svg width={isMobile ? "100%" : "120%"} height="100%" style={{ position: 'absolute', top: 0, left: 0 }}>
      {points.map((point, index) => (
        <motion.circle
          key={index}
          cx={point.x}
          cy={point.y}
          r={isMobile ? 2 : 3}
          fill="rgba(255,255,255,0.4)"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ 
            opacity: isVisible ? 1 : 0,
            scale: isVisible ? 1 : 0
          }}
          transition={{ 
            duration: 0.3, 
            delay: isVisible ? index * 0.03 : 0 
          }}
        />
      ))}
    </svg>
  );
};

const CapabilitiesSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // State for visibility of each element
  const [capability1Visible, setCapability1Visible] = useState(false);
  const [capability2Visible, setCapability2Visible] = useState(false);
  const [capability3Visible, setCapability3Visible] = useState(false);
  const [capability4Visible, setCapability4Visible] = useState(false);
  const [capability5Visible, setCapability5Visible] = useState(false);
  
  const [line1Visible, setLine1Visible] = useState(false);
  const [line2Visible, setLine2Visible] = useState(false);
  const [line3Visible, setLine3Visible] = useState(false);
  const [line4Visible, setLine4Visible] = useState(false);
  
  // Refs for scroll detection
  const sectionRef = useRef(null);
  const capability1Ref = useRef(null);
  const capability2Ref = useRef(null);
  const capability3Ref = useRef(null);
  const capability4Ref = useRef(null);
  const capability5Ref = useRef(null);
  
  const line1Ref = useRef(null);
  const line2Ref = useRef(null);
  const line3Ref = useRef(null);
  const line4Ref = useRef(null);

  // Function to create an intersection observer
  const createObserver = (ref, setVisible) => {
    if (!ref.current) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(entry.target); // Stop observing once visible
        }
      },
      { 
        threshold: isMobile ? 0.1 : 0.3, // Lower threshold for mobile
        rootMargin: isMobile ? "0px 0px -50px 0px" : "0px 0px -100px 0px" // Adjusted for mobile
      }
    );
    
    observer.observe(ref.current);
    return observer;
  };

  // Set up observers when component mounts
  useEffect(() => {
    const observers = [];
    
    // Create observers for capabilities
    observers.push(createObserver(capability1Ref, setCapability1Visible));
    observers.push(createObserver(capability2Ref, setCapability2Visible));
    observers.push(createObserver(capability3Ref, setCapability3Visible));
    observers.push(createObserver(capability4Ref, setCapability4Visible));
    observers.push(createObserver(capability5Ref, setCapability5Visible));
    
    // Create observers for lines
    observers.push(createObserver(line1Ref, setLine1Visible));
    observers.push(createObserver(line2Ref, setLine2Visible));
    observers.push(createObserver(line3Ref, setLine3Visible));
    observers.push(createObserver(line4Ref, setLine4Visible));
    
    // Cleanup function
    return () => {
      observers.forEach(observer => {
        if (observer) observer.disconnect();
      });
    };
  }, [isMobile]);

  // Calculate line positions based on screen size
  const getLinePositions = () => {
    if (isMobile) {
      return {
        line1: { top: '120px', height: '200px', startX: 50, startY: 50, endX: 150, endY: 200 },
        line2: { top: '320px', height: '200px', startX: 150, startY: 50, endX: 50, endY: 200 },
        line3: { top: '520px', height: '200px', startX: 50, startY: 50, endX: 150, endY: 200 },
      };
    } else if (isTablet) {
      return {
        line1: { top: '50px', height: '300px', startX: 150, startY: 100, endX: 400, endY: 300 },
        line2: { top: '350px', height: '300px', startX: 400, startY: 100, endX: 150, endY: 300 },
        line3: { top: '650px', height: '300px', startX: 150, startY: 100, endX: 400, endY: 300 },
      };
    } else {
      return {
        line1: { top: '50px', height: '400px', startX: 200, startY: 100, endX: 900, endY: 400 },
        line2: { top: '350px', height: '500px', startX: 900, startY: 100, endX: 200, endY: 500 },
        line3: { top: '850px', height: '700px', startX: 200, startY: 20, endX: 1000, endY: 500 }, 
      };
    }
  };

  const linePositions = getLinePositions();

  return (
    <Box
      ref={sectionRef}
      sx={{
        bgcolor: '#000000',
        py: { xs: 8, md: 15 },
        position: 'relative',
        overflow: 'hidden',
        backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)',
        backgroundSize: { xs: '40px 40px', md: '100px 100px' },
        minHeight: { xs: '120vh', sm: '150vh', md: '120vh' },
        height: '200vh',
        height:{ xs: isMobile ? '1100px' : '1400px', md: '1800px' },
      }}
    >
      <Container maxWidth="xl" sx={{ position: 'relative' }}>
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              color: '#fad32e',
              fontWeight: 'bold',
              fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3.5rem' },
              mb: { xs: 5, md: 12 },
            }}
          >
            What Can You Do with Hashinfra?
          </Typography>
        </motion.div>

        <Box sx={{ 
          position: 'relative', 
          height: { xs: isMobile ? '1000px' : '1200px', md: '1800px' },
          // Add padding on mobile to prevent content from touching edges
          px: { xs: 2, sm: 0 }
        }}>
          {/* All dotted lines */}
          {/* Line 1: 1 to 2 */}
          <Box
            ref={line1Ref}
            sx={{
              position: 'absolute',
              top: linePositions.line1.top,
              left: '0',
              width: {xs: '100%', md: '120%' },
              height: linePositions.line1.height,
              zIndex: 1,
              display: { xs: isMobile ? 'none' : 'block', md: 'block' }, // Hide on very small mobile
            }}
          >
            <DottedLine 
              startX={linePositions.line1.startX} 
              startY={linePositions.line1.startY} 
              endX={linePositions.line1.endX} 
              endY={linePositions.line1.endY} 
              numDots={isMobile ? 15 : 30} 
              isVisible={line1Visible}
              direction="right"
              isMobile={isMobile}
            />
          </Box>

          {/* Line 2: 2 to 3 */}
          <Box
            ref={line2Ref}
            sx={{
              position: 'absolute',
              top: linePositions.line2.top,
              left: '0',
              width: '100%',
              height: linePositions.line2.height,
              zIndex: 1,
              display: { xs: isMobile ? 'none' : 'block', md: 'block' }, // Hide on very small mobile
            }}
          >
            <DottedLine 
              startX={linePositions.line2.startX} 
              startY={linePositions.line2.startY} 
              endX={linePositions.line2.endX} 
              endY={linePositions.line2.endY} 
              numDots={isMobile ? 15 : 30} 
              isVisible={line2Visible}
              direction="left"
              isMobile={isMobile}
            />
          </Box>

          {/* Line 3: 3 to 4 */}
          <Box
            ref={line3Ref}
            sx={{
              position: 'absolute',
              top: linePositions.line3.top,
              left: '0',
              width: '100%',
              height: linePositions.line3.height,
              zIndex: 1,
              display: { xs: isMobile ? 'none' : 'block', md: 'block' }, // Hide on very small mobile
            }}
          >
            <DottedLine 
              startX={linePositions.line3.startX} 
              startY={linePositions.line3.startY} 
              endX={linePositions.line3.endX} 
              endY={linePositions.line3.endY} 
              numDots={isMobile ? 15 : 30} 
              isVisible={line3Visible}
              direction="right"
              isMobile={isMobile}
            />
          </Box>

          

          {/* Feature 1 */}
          <motion.div
            ref={capability1Ref}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: capability1Visible ? 1 : 0, x: capability1Visible ? 0 : -50 }}
            transition={{ duration: 0.8 }}
            style={{ 
              position: 'relative',
              marginBottom: isMobile ? '60px' : '100px'
            }}
          >
            <Grid container sx={{ position: 'relative' }}>
              <Grid item xs={12} md={4} sx={{ 
                position: 'relative',
                mb: { xs: 2, md: 0 }
              }}>
                <Box
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: { xs: 'flex-start', md: 'flex-end' },
                    pr: { md: 6 },
                  }}
                >
                  <Typography
                    variant="h1"
                    sx={{
                      color: 'rgba(255,255,255,0.15)',
                      fontWeight: 'regular',
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: { xs: '5rem', sm: '8rem', md: '10rem' },
                      lineHeight: 0.8,
                      mb: { xs: 2, md: 0 },
                    }}
                  >
                    1
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={8}>
                <Box
                  sx={{
                    position: 'relative',
                    mt: { xs: 0, md: 6 },
                    ml: { xs: 0, md: -4 },
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#ffffff',
                      fontWeight: 'normal',
                      fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                      lineHeight: 1.4,
                      textAlign: { xs: 'left', md: 'left' },
                      maxWidth: { xs: '100%', sm: '80%', md: '400px' },
                      fontFamily: 'Poppins, sans-serif',
                    }}
                  >
                    Launch Multi-Cluster Environments in minutes across major cloud providers.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </motion.div>

          {/* Feature 2 */}
          <motion.div
            ref={capability2Ref}
            initial={{ opacity: 0, x: isMobile ? -50 : 50 }} // Change animation direction on mobile
            animate={{ opacity: capability2Visible ? 1 : 0, x: capability2Visible ? 0 : (isMobile ? -50 : 50) }}
            transition={{ duration: 0.8 }}
            style={{ 
              position: 'relative',
              marginBottom: isMobile ? '60px' : '150px'
            }}
          >
            <Grid container sx={{ 
              position: 'relative', 
              flexDirection: { xs: 'column-reverse', md: 'row' },
              justifyContent: { md: 'flex-end' }
            }}>
              <Grid item xs={12} md={8} sx={{ 
                display: 'flex', 
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                mb: { xs: 2, md: 0 }
              }}>
                <Box
                  sx={{
                    position: 'relative',
                    mt: { xs: 0, md: 6 },
                    mr: { xs: 0, md: -4 },
                    textAlign: { xs: 'left', md: 'right' },
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#ffffff',
                      fontWeight: 'normal',
                      fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                      lineHeight: 1.4,
                      textAlign: { xs: 'left', md: 'right' },
                      maxWidth: { xs: '100%', sm: '80%', md: '400px' },
                      fontFamily: 'Poppins, sans-serif',
                    }}
                  >
                    Set Up Secure CI/CD workflows with GitHub, GitLab, or Bitbucket.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4} sx={{ position: 'relative' }}>
                <Box
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: { xs: 'flex-start', md: 'flex-start' },
                    pl: { md: 6 },
                  }}
                >
                  <Typography
                    variant="h1"
                    sx={{
                      color: 'rgba(255,255,255,0.15)',
                      fontWeight: 'regular',
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: { xs: '5rem', sm: '8rem', md: '10rem' },
                      lineHeight: 0.8,
                      mb: { xs: 2, md: 0 },
                    }}
                  >
                    2
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </motion.div>

          {/* Feature 3 */}
          <motion.div
            ref={capability3Ref}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: capability3Visible ? 1 : 0, x: capability3Visible ? 0 : -50 }}
            transition={{ duration: 0.8 }}
            style={{ 
              position: 'relative',
              marginTop: isMobile ? '20px' : '50px',
              marginBottom: isMobile ? '60px' : '150px'
            }}
          >
            <Grid container sx={{ position: 'relative' }}>
              <Grid item xs={12} md={4} sx={{ 
                position: 'relative',
                mb: { xs: 2, md: 0 }
              }}>
                <Box
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: { xs: 'flex-start', md: 'flex-end' },
                    pr: { md: 6 },
                  }}
                >
                  <Typography
                    variant="h1"
                    sx={{
                      color: 'rgba(255,255,255,0.15)',
                      fontWeight: 'regular',
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: { xs: '5rem', sm: '8rem', md: '10rem' },
                      lineHeight: 0.8,
                      mb: { xs: 2, md: 0 },
                    }}
                  >
                    3
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={8}>
                <Box
                  sx={{
                    position: 'relative',
                    mt: { xs: 0, md: 6 },
                    ml: { xs: 0, md: -4 },
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#ffffff',
                      fontWeight: 'normal',
                      fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                      lineHeight: 1.4,
                      textAlign: { xs: 'left', md: 'left' },
                      maxWidth: { xs: '100%', sm: '80%', md: '400px' },
                      fontFamily: 'Poppins, sans-serif',
                    }}
                  >
                    Automate Deployments for microservices, APIs, or monoliths — at scale.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </motion.div>

          {/* Feature 4 */}
          <motion.div
            ref={capability4Ref}
            initial={{ opacity: 0, x: isMobile ? -50 : 50 }} // Change animation direction on mobile
            animate={{ opacity: capability4Visible ? 1 : 0, x: capability4Visible ? 0 : (isMobile ? -50 : 50) }}
            transition={{ duration: 0.8 }}
            style={{ 
              position: 'relative',
              marginBottom: isMobile ? '60px' : '150px'
            }}
          >
            <Grid container sx={{ 
              position: 'relative', 
              flexDirection: { xs: 'column-reverse', md: 'row' },
              justifyContent: { md: 'flex-end' }
            }}>
              <Grid item xs={12} md={8} sx={{ 
                display: 'flex', 
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                mb: { xs: 2, md: 0 }
              }}>
                <Box
                  sx={{
                    position: 'relative',
                    mt: { xs: 0, md: 6 },
                    mr: { xs: 0, md: -4 },
                    textAlign: { xs: 'left', md: 'right' },
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#ffffff',
                      fontWeight: 'normal',
                      fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                      lineHeight: 1.4,
                      textAlign: { xs: 'left', md: 'right' },
                      maxWidth: { xs: '100%', sm: '80%', md: '400px' },
                      fontFamily: 'Poppins, sans-serif',
                    }}
                  >
                    Monitor, Alert, and Upgrade infrastructure with built-in observability.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4} sx={{ position: 'relative' }}>
                <Box
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: { xs: 'flex-start', md: 'flex-start' },
                    pl: { md: 6 },
                  }}
                >
                  <Typography
                    variant="h1"
                    sx={{
                      color: 'rgba(255,255,255,0.15)',
                      fontWeight: 'regular',
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: { xs: '5rem', sm: '8rem', md: '10rem' },
                      lineHeight: 0.8,
                      mb: { xs: 2, md: 0 },
                    }}
                  >
                    4
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </motion.div>

          {/* Feature 5 */}
          <motion.div
            ref={capability5Ref}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: capability5Visible ? 1 : 0, x: capability5Visible ? 0 : -50 }}
            transition={{ duration: 0.8 }}
            style={{ 
              position: 'relative',
              marginTop: isMobile ? '20px' : '50px'
            }}
          >
            <Grid container sx={{ position: 'relative' }}>
              <Grid item xs={12} md={4} sx={{ 
                position: 'relative',
                mb: { xs: 2, md: 0 }
              }}>
                <Box
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: { xs: 'flex-start', md: 'flex-end' },
                    pr: { md: 6 },
                  }}
                >
                  <Typography
                    variant="h1"
                    sx={{
                      color: 'rgba(255,255,255,0.15)',
                      fontWeight: 'regular',
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: { xs: '5rem', sm: '8rem', md: '10rem' },
                      lineHeight: 0.8,
                      mb: { xs: 2, md: 0 },
                    }}
                  >
                    5
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={8}>
                <Box
                  sx={{
                    position: 'relative',
                    mt: { xs: 0, md: 6 },
                    ml: { xs: 0, md: -4 },
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#ffffff',
                      fontWeight: 'normal',
                      fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                      lineHeight: 1.4,
                      textAlign: { xs: 'left', md: 'left' },
                      maxWidth: { xs: '100%', sm: '80%', md: '400px' },
                      fontFamily: 'Poppins, sans-serif',
                    }}
                  >
                    Get Expert Support — based on your plan.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </motion.div>
        </Box>
      </Container>
    </Box>
  );
};

export default CapabilitiesSection;

